'use client';

import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface GeneratedReport {
  id: string;
  name: string;
  framework: string;
  generatedAt: string;
  generatedBy: string;
  format: 'pdf' | 'csv' | 'json';
  size: string;
  status: 'ready' | 'generating' | 'failed';
  dateRange: string;
  pages?: number;
}

const reports: GeneratedReport[] = [
  { id: 'RPT-2026-0147', name: 'ISO 27001 — SecurityIncident Register', framework: 'iso27001', generatedAt: '2026-03-16 07:00 UTC', generatedBy: 'System (Scheduled)', format: 'pdf', size: '2.4 MB', status: 'ready', dateRange: '2026-03-09 – 2026-03-16', pages: 22 },
  { id: 'RPT-2026-0146', name: 'Daily Threat Summary', framework: 'operational', generatedAt: '2026-03-16 06:00 UTC', generatedBy: 'System (Scheduled)', format: 'pdf', size: '890 KB', status: 'ready', dateRange: '2026-03-15', pages: 6 },
  { id: 'RPT-2026-0145', name: 'PCI-DSS Requirement 10 — Audit Log Report', framework: 'pci-dss', generatedAt: '2026-03-15 06:00 UTC', generatedBy: 'System (Scheduled)', format: 'csv', size: '14.2 MB', status: 'ready', dateRange: '2026-03-08 – 2026-03-15' },
  { id: 'RPT-2026-0144', name: 'Asset Risk Posture Report', framework: 'operational', generatedAt: '2026-03-15 00:00 UTC', generatedBy: 'Selin Arslan', format: 'pdf', size: '3.1 MB', status: 'ready', dateRange: '2026-03-15', pages: 20 },
  { id: 'RPT-2026-0143', name: 'SOC 2 Type II — Security Monitoring Evidence', framework: 'soc2', generatedAt: '2026-03-15 00:00 UTC', generatedBy: 'Mehmet Yılmaz', format: 'pdf', size: '8.7 MB', status: 'ready', dateRange: '2026-01-01 – 2026-03-15', pages: 45 },
  { id: 'RPT-2026-0142', name: 'Network Traffic Anomaly Report', framework: 'operational', generatedAt: '2026-03-14 00:00 UTC', generatedBy: 'System (Scheduled)', format: 'csv', size: '5.3 MB', status: 'ready', dateRange: '2026-03-07 – 2026-03-14' },
  { id: 'RPT-2026-0141', name: 'Alert Rule Performance Report', framework: 'operational', generatedAt: '2026-03-13 08:00 UTC', generatedBy: 'Ayşe Kaya', format: 'pdf', size: '1.8 MB', status: 'ready', dateRange: '2026-03-06 – 2026-03-13', pages: 14 },
  { id: 'RPT-2026-0140', name: 'MITRE ATT&CK Coverage Map', framework: 'operational', generatedAt: '2026-03-12 09:00 UTC', generatedBy: 'Selin Arslan', format: 'json', size: '340 KB', status: 'ready', dateRange: '2026-03-12' },
  { id: 'RPT-2026-0139', name: 'Daily Threat Summary', framework: 'operational', generatedAt: '2026-03-15 06:00 UTC', generatedBy: 'System (Scheduled)', format: 'pdf', size: '910 KB', status: 'generating', dateRange: '2026-03-15', pages: 6 },
  { id: 'RPT-2026-0138', name: 'ISO 27001 — ISMS Performance Metrics', framework: 'iso27001', generatedAt: '2026-03-01 00:00 UTC', generatedBy: 'Mehmet Yılmaz', format: 'pdf', size: '2.1 MB', status: 'ready', dateRange: '2026-02-01 – 2026-03-01', pages: 18 },
  { id: 'RPT-2026-0137', name: 'PCI-DSS Vulnerability Scan Summary', framework: 'pci-dss', generatedAt: '2026-03-10 08:00 UTC', generatedBy: 'System (Scheduled)', format: 'pdf', size: '1.4 MB', status: 'ready', dateRange: '2026-03-03 – 2026-03-10', pages: 14 },
  { id: 'RPT-2026-0136', name: 'Weekly SOC Operations Report', framework: 'operational', generatedAt: '2026-03-09 08:00 UTC', generatedBy: 'System (Scheduled)', format: 'pdf', size: '2.8 MB', status: 'failed', dateRange: '2026-03-02 – 2026-03-09', pages: 12 },
];

const frameworkColors: Record<string, string> = {
  'pci-dss': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  'iso27001': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  'soc2': 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  'operational': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
};

const frameworkLabels: Record<string, string> = {
  'pci-dss': 'PCI-DSS',
  'iso27001': 'ISO 27001',
  'soc2': 'SOC 2',
  'operational': 'Operational',
};

const formatColors: Record<string, string> = {
  pdf: 'text-red-400',
  csv: 'text-emerald-400',
  json: 'text-blue-400',
};

const statusConfig: Record<string, { label: string; classes: string; icon: string }> = {
  ready: { label: 'Ready', classes: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20', icon: 'CheckCircleIcon' },
  generating: { label: 'Generating…', classes: 'bg-amber-500/15 text-amber-400 border border-amber-500/20', icon: 'ArrowPathIcon' },
  failed: { label: 'Failed', classes: 'bg-red-500/15 text-red-400 border border-red-500/20', icon: 'XCircleIcon' },
};

interface RecentReportsTableProps {
  limit?: number;
}

export default function RecentReportsTable({ limit = 10 }: RecentReportsTableProps) {
  const displayReports = reports.slice(0, limit);

  const handleDownload = (report: GeneratedReport) => {
    // Backend integration: GET /api/reports/:id/download
    toast.success(`Downloading ${report.id}.${report.format}`);
  };

  const handleRetry = (report: GeneratedReport) => {
    // Backend integration: POST /api/reports/:id/retry
    toast.success(`Re-queued ${report.name} for generation`);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800">
        <h3 className="text-sm font-semibold text-zinc-200">Report History</h3>
        <span className="text-xs text-zinc-600 font-mono">{reports.length} reports total</span>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Report ID', 'Report Name', 'Framework', 'Date Range', 'Generated', 'By', 'Format', 'Size', 'Pages', 'Status', 'Actions'].map((h) => (
                <th key={h} className="px-3 py-3 text-left font-semibold text-zinc-600 uppercase tracking-widest text-[10px] whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayReports.map((report, idx) => {
              const stCfg = statusConfig[report.status];
              return (
                <tr
                  key={report.id}
                  className={`border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors group ${idx % 2 !== 0 ? 'bg-zinc-900/30' : ''}`}
                >
                  <td className="px-3 py-3 font-mono text-zinc-500 whitespace-nowrap">{report.id}</td>
                  <td className="px-3 py-3 max-w-xs">
                    <p className="text-zinc-200 font-medium truncate">{report.name}</p>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wide ${frameworkColors[report.framework]}`}>
                      {frameworkLabels[report.framework]}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-mono text-zinc-600 whitespace-nowrap text-[11px]">{report.dateRange}</td>
                  <td className="px-3 py-3 font-mono text-zinc-500 whitespace-nowrap text-[11px]">{report.generatedAt}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-zinc-500 text-[11px]">{report.generatedBy.split(' (')[0]}</span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`font-mono text-xs font-bold uppercase ${formatColors[report.format]}`}>{report.format}</span>
                  </td>
                  <td className="px-3 py-3 font-mono text-zinc-600 whitespace-nowrap">{report.size}</td>
                  <td className="px-3 py-3 font-mono text-zinc-600 whitespace-nowrap">{report.pages ? `${report.pages}p` : '—'}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${stCfg.classes}`}>
                      <Icon name={stCfg.icon as any} size={10} className={report.status === 'generating' ? 'animate-spin' : ''} />
                      {stCfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      {report.status === 'ready' && (
                        <div className="relative group/tip">
                          <button
                            onClick={() => handleDownload(report)}
                            className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-300 transition-colors"
                            aria-label="Download report"
                          >
                            <Icon name="ArrowDownTrayIcon" size={13} />
                          </button>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">
                            Download {report.format.toUpperCase()}
                          </span>
                        </div>
                      )}
                      {report.status === 'failed' && (
                        <div className="relative group/tip">
                          <button
                            onClick={() => handleRetry(report)}
                            className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-amber-400 transition-colors"
                            aria-label="Retry report generation"
                          >
                            <Icon name="ArrowPathIcon" size={13} />
                          </button>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">
                            Retry generation
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}